import { NgFor, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { StorageService } from '../../../services/storage.service';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nav-bar',
  imports: [NgFor, NgIf, RouterLink],
  templateUrl: './nav-bar.component.html',
  styleUrl: './nav-bar.component.css'
})
export class NavBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('categoryScroller') categoryScroller?: ElementRef<HTMLDivElement>;
  @ViewChild('moreTrigger') moreTrigger?: ElementRef<HTMLElement>;
  @ViewChild('moreMenu') moreMenu?: ElementRef<HTMLElement>;
  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  isMoreMenuOpen: boolean = false;
  isMoreMenuPositioned: boolean = false;
  moreMenuLeft: number = 0;
  moreMenuTop: number = 0;
  private resizeObserver?: ResizeObserver;
  private closeMenuTimer?: ReturnType<typeof setTimeout>;
  // A pointer that travelled this far between press and release was a swipe, so
  // the click the browser synthesises afterwards is not a real tap on an item.
  private static readonly DRAG_THRESHOLD_PX: number = 8;
  // A click landing this soon after hover opened the dropdown belongs to the
  // same gesture that opened it, so it must not toggle the dropdown shut.
  private static readonly HOVER_CLICK_GRACE_MS: number = 400;
  private hoverOpenedAt: number = 0;
  private pointerStartX: number = 0;
  private pointerStartY: number = 0;
  private pointerStartScrollLeft: number = 0;
  private isPointerDown: boolean = false;
  private suppressNextClick: boolean = false;
  // The storage streams live as long as the app does, so every subscription is
  // collected here and torn down with the component.
  private readonly subscriptions: Subscription = new Subscription();
  get selectedCategory(): string {
    return this.storage.getSelectedCategory() || 'all';
  }
  @Input() categories: Array<any> = [];
  @Output() sendSelectedCategory: EventEmitter<{ category: string; option: string }> = new EventEmitter()
  // defaultCategory: Array<any> = [
  //   { name: "Trending", id: "trending" },
  //   { name: "Breaking", id: "breaking" },
  //   { name: "New", id: "new" }
  // ];

  constructor(private storage: StorageService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) { }

  ngOnInit(): void {
    // listening category api updates from storage service.
    this.categories = this.storage.getCategoryData();
    this.onCategoriesChanged();
    this.subscriptions.add(this.storage
      .categoryUpdate$
      .subscribe((result: boolean) => {
        if (result === true) {
          this.categories = this.storage.getCategoryData();
          this.onCategoriesChanged();

          if (this.storage.getSelectedCategory()) {
            this.selectCategory(this.storage.getSelectedCategory(), 'dynamic', false);
          } else {
            this.selectCategory('all', 'default', false);
          }
        }
      }));

    // The mobile tab bar lives outside the home page, so it routes its
    // category taps through the storage service instead of an @Output.
    this.subscriptions.add(this.storage
      .categorySelection$
      .subscribe(({ category, option }: { category: string; option: string }) => {
        this.selectCategory(category, option, false);
      }));
  }

  /**
   * Re-evaluates the arrows once the new category items are painted. Every
   * category is listed inline, so the arrows are the only way to reach the
   * ones that overflow.
   * @returns {void}
   */
  private onCategoriesChanged(): void {
    setTimeout(() => this.updateArrowState());
  }

  ngAfterViewInit(): void {
    const scroller = this.categoryScroller?.nativeElement;
    if (!scroller) {
      return;
    }

    // Re-evaluate the arrows whenever the viewport or the category list resizes.
    this.resizeObserver = new ResizeObserver(() => this.updateArrowState());
    this.resizeObserver.observe(scroller);
    if (scroller.firstElementChild) {
      this.resizeObserver.observe(scroller.firstElementChild);
    }
    this.updateArrowState();

    // Swipe bookkeeping only flips a private flag, so it is kept out of the zone
    // instead of running change detection on every pointer move.
    this.zone.runOutsideAngular(() => {
      scroller.addEventListener('pointerdown', this.onPointerDown);
      // Captured before the template's (click) handlers so a click left over
      // from a swipe can still be swallowed.
      scroller.addEventListener('click', this.onClickCapture, true);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerCancel);
    });
  }

  /**
   * Starts tracking a possible swipe over the category list.
   * @param {PointerEvent} event - The pointerdown event on the scroller.
   * @returns {void}
   */
  private readonly onPointerDown = (event: PointerEvent): void => {
    this.isPointerDown = true;
    this.suppressNextClick = false;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.pointerStartScrollLeft = this.categoryScroller?.nativeElement.scrollLeft ?? 0;
  };

  /**
   * Marks the gesture as a swipe once the pointer leaves the tap threshold.
   * @param {PointerEvent} event - The pointermove event.
   * @returns {void}
   */
  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.isPointerDown || this.suppressNextClick) {
      return;
    }
    const movedX = Math.abs(event.clientX - this.pointerStartX);
    const movedY = Math.abs(event.clientY - this.pointerStartY);
    if (movedX > NavBarComponent.DRAG_THRESHOLD_PX || movedY > NavBarComponent.DRAG_THRESHOLD_PX) {
      this.suppressNextClick = true;
    }
  };

  /**
   * Ends the gesture, treating it as a swipe if the list actually moved.
   * @returns {void}
   */
  private readonly onPointerUp = (): void => {
    if (!this.isPointerDown) {
      return;
    }
    const scroller = this.categoryScroller?.nativeElement;
    if (scroller && Math.abs(scroller.scrollLeft - this.pointerStartScrollLeft) > 1) {
      this.suppressNextClick = true;
    }
    this.isPointerDown = false;
  };

  /**
   * The browser took the gesture over to scroll the list, so any click that
   * follows belongs to the swipe rather than to the item under the finger.
   * @returns {void}
   */
  private readonly onPointerCancel = (): void => {
    if (!this.isPointerDown) {
      return;
    }
    this.suppressNextClick = true;
    this.isPointerDown = false;
  };

  /**
   * Swallows the click a swipe leaves behind, so it never reaches a category or
   * the "More" trigger.
   * @param {MouseEvent} event - The captured click event.
   * @returns {void}
   */
  private readonly onClickCapture = (event: MouseEvent): void => {
    if (!this.suppressNextClick) {
      return;
    }
    this.suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.resizeObserver?.disconnect();
    this.clearCloseTimer();
    this.categoryScroller?.nativeElement.removeEventListener('pointerdown', this.onPointerDown);
    this.categoryScroller?.nativeElement.removeEventListener('click', this.onClickCapture, true);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
  }

  /**
   * Keeps the arrows in sync while the list is scrolled and keeps the open
   * dropdown anchored to the "More" item, which moves along with the list.
   * @returns {void}
   */
  onScrollerScroll(): void {
    this.updateArrowState();
    if (this.isMoreMenuOpen) {
      this.positionMoreMenu();
    }
  }

  /**
   * Opens the dropdown when a mouse hovers the "More" item. Touch and pen are
   * ignored: a tap emulates hover before it fires its click, so opening here
   * would let the click that follows toggle the dropdown straight back shut and
   * swallow the first tap.
   * @param {PointerEvent} event - The pointerenter event on the trigger.
   * @returns {void}
   */
  onMoreTriggerPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      return;
    }
    const wasOpen = this.isMoreMenuOpen;
    this.openMoreMenu();
    if (!wasOpen && this.isMoreMenuOpen) {
      this.hoverOpenedAt = Date.now();
    }
  }

  /**
   * Closes the dropdown when the mouse leaves the "More" item. Touch and pen
   * are ignored; those close through the outside-click listener instead.
   * @param {PointerEvent} event - The pointerleave event on the trigger.
   * @returns {void}
   */
  onMoreTriggerPointerLeave(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      return;
    }
    this.scheduleCloseMoreMenu();
  }

  /**
   * Opens the "More" dropdown, used by both hover and click.
   * @returns {void}
   */
  openMoreMenu(): void {
    this.clearCloseTimer();
    if (this.isMoreMenuOpen) {
      return;
    }
    this.isMoreMenuOpen = true;
    // The dropdown is measured before it is shown, so it is painted at its final
    // position instead of flashing off the edge of the screen for one frame.
    this.isMoreMenuPositioned = false;
    // Rendered right here rather than waiting for the coalesced change detection
    // pass: that one runs in a later animation frame, so measuring before it has
    // run would find no dropdown at all and leave the menu open but invisible.
    this.cdr.detectChanges();
    this.positionMoreMenu();
  }

  /**
   * Closes the "More" dropdown.
   * @returns {void}
   */
  closeMoreMenu(): void {
    this.clearCloseTimer();
    this.isMoreMenuOpen = false;
    this.isMoreMenuPositioned = false;
    this.hoverOpenedAt = 0;
  }

  /**
   * Toggles the dropdown on click / tap, where hover is not available.
   * @returns {void}
   */
  toggleMoreMenu(): void {
    if (!this.isMoreMenuOpen) {
      this.hoverOpenedAt = 0;
      this.openMoreMenu();
      return;
    }

    // Hover has just opened the dropdown as part of this same gesture - the
    // pointer moving onto "More", or "More" appearing under a resting cursor at
    // the end of a scroll. Closing it here would read as the click doing
    // nothing, so the click is spent on keeping the dropdown open instead.
    if (Date.now() - this.hoverOpenedAt < NavBarComponent.HOVER_CLICK_GRACE_MS) {
      this.hoverOpenedAt = 0;
      this.clearCloseTimer();
      return;
    }
    this.closeMoreMenu();
  }

  /**
   * Closes the dropdown after a short grace period so the pointer can travel
   * from the "More" item to the dropdown without it disappearing.
   * @returns {void}
   */
  scheduleCloseMoreMenu(): void {
    this.clearCloseTimer();
    this.closeMenuTimer = setTimeout(() => this.closeMoreMenu(), 150);
  }

  private clearCloseTimer(): void {
    if (this.closeMenuTimer) {
      clearTimeout(this.closeMenuTimer);
      this.closeMenuTimer = undefined;
    }
  }

  /**
   * Anchors the fixed-positioned dropdown under the "More" item. The dropdown
   * cannot be absolutely positioned because the category list clips overflow.
   * @returns {void}
   */
  private positionMoreMenu(): void {
    const trigger = this.moreTrigger?.nativeElement;
    const menu = this.moreMenu?.nativeElement;
    if (!trigger || !menu) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;
    const gutter = 8;
    // clientWidth, not innerWidth: the latter counts the scrollbar, which would
    // let the dropdown slide underneath it.
    const viewportWidth = document.documentElement.clientWidth;
    const maxLeft = viewportWidth - menuWidth - gutter;

    // "More" is the last item of the category row, so the dropdown is aligned to
    // its right edge and only falls back to left alignment when the row is short
    // enough that right alignment would push it off the left edge.
    const rightAligned = rect.right - menuWidth;
    const preferredLeft = rightAligned < gutter ? rect.left : rightAligned;
    this.moreMenuLeft = Math.max(gutter, Math.min(preferredLeft, maxLeft));
    this.moreMenuTop = rect.bottom;
    this.isMoreMenuPositioned = true;
    // Pushes the new offsets onto the element in this frame, so the dropdown is
    // never left sitting at its previous place waiting for the next pass.
    this.cdr.detectChanges();
  }

  /**
   * Closes the dropdown when clicking anywhere outside the nav bar.
   * @param {MouseEvent} event - The document click event.
   * @returns {void}
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isMoreMenuOpen) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.closeMoreMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMoreMenu();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateArrowState();
    this.closeMoreMenu();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isMoreMenuOpen) {
      this.positionMoreMenu();
    }
  }

  /**
   * Scrolls the category list horizontally by ~70% of the visible width.
   * @param {number} direction - -1 to scroll left, 1 to scroll right.
   * @returns {void}
   */
  scrollCategories(direction: number): void {
    const scroller = this.categoryScroller?.nativeElement;
    if (!scroller) {
      return;
    }
    scroller.scrollBy({ left: direction * scroller.clientWidth * 0.7, behavior: 'smooth' });
  }

  /**
   * Shows or hides the arrows based on the remaining scrollable space.
   * @returns {void}
   */
  updateArrowState(): void {
    const scroller = this.categoryScroller?.nativeElement;
    if (!scroller) {
      return;
    }
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    this.canScrollLeft = scroller.scrollLeft > 1;
    this.canScrollRight = scroller.scrollLeft < maxScrollLeft - 1;

    // Scrolling back left hides the "More" item, so an open dropdown would be
    // left anchored to a trigger the user can no longer see.
    if (this.canScrollRight && this.isMoreMenuOpen) {
      this.closeMoreMenu();
    }
  }

  /**
   * Selects a category to display in the main component.
   * @param {string} category - The category to select.
   * @param {string} [option='default'] - The option to select.
   * @param {boolean} [redirect=true] - Whether to redirect to the homepage after selecting the category.
   * @returns {void}
   */
  selectCategory(category: string, option: string = 'default', redirect: boolean = true): void {
    this.storage.setSelectedCategory(category);
    this.closeMoreMenu();

    if (redirect) {
      this.router.navigate(['/']);
    }
    this.sendSelectedCategory.next({ category, option });
  }
}
